import axios from 'axios';
import { User, AuthResponse } from '../../types';

function decodeToken(t: string | null): any {
  if (!t) return null;
  try {
    const part = t.indexOf('.') >= 0 ? t.split('.')[0] : t;
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    return JSON.parse(atob(b64));
  } catch { return null; }
}

const API_BASE     = 'https://nexus-hr.talenthubupskilling.com/backend/hr-api';
const OPTION_C_BASE = 'https://nexus-hr.talenthubupskilling.com/backend/api/auth';

class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  constructor() {
    this.loadTokensFromStorage();
  }

  // ── EXISTING: Employee Authentication (unchanged) ─────────────────────────
  async employeeLogin(firstName: string, lastName: string, employeeIdOrEmail: string, organizationId: string): Promise<AuthResponse> {
    try {
      const isEmail = employeeIdOrEmail.includes('@');
      const payload = isEmail
        ? { email: employeeIdOrEmail }
        : { first_name: firstName, last_name: lastName, employee_id: employeeIdOrEmail, tenant_id: organizationId };

      const response = await axios.post(`${API_BASE}/auth/login.php`, payload);

      if (response.data.success) {
        const authResponse: AuthResponse = {
          success: true, user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          expires_in: response.data.expires_in
        };
        this.setTokens(authResponse.access_token, authResponse.refresh_token);
        if (response.data.user?.tenant_id)
          localStorage.setItem('hr_tenant_id', response.data.user.tenant_id);
        return authResponse;
      }
      throw new Error(response.data.error || 'Employee login failed');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error)
        throw new Error(error.response.data.error);
      throw new Error('Employee login failed. Please check your credentials.');
    }
  }

  // ── EXISTING: Admin login (unchanged — keeps 22 live tenants working) ─────
  async login(adminEmail: string, adminPassword: string): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE}/auth/admin-login.php`, {
        admin_email: adminEmail, admin_password: adminPassword
      });
      if (response.data.success) {
        this.setTokens(response.data.access_token, response.data.refresh_token);
        if (response.data.user?.tenant_id)
          localStorage.setItem('hr_tenant_id', response.data.user.tenant_id);
        return response.data;
      }
      throw new Error(response.data.error || 'Authentication failed');
    } catch (error: any) {
      if (error instanceof Error && error.message) {
        throw error;
      }
      const backendMessage = error?.response?.data?.error;
      throw new Error(backendMessage || 'Login failed. Please check your credentials.');
    }
  }

  // ── EXISTING: Registration (unchanged) ────────────────────────────────────
  async register(registrationData: any): Promise<AuthResponse> {
    try {
      const response = await axios.post(`${API_BASE}/auth/register.php`, registrationData);
      if (response.data.success) {
        const authResponse: AuthResponse = {
          success: true, user: response.data.user,
          access_token: response.data.access_token,
          refresh_token: response.data.refresh_token,
          expires_in: response.data.expires_in
        };
        this.setTokens(authResponse.access_token, authResponse.refresh_token);
        return authResponse;
      }
      throw new Error(response.data.error || 'Registration failed');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error)
        throw new Error(error.response.data.error);
      throw new Error('Registration failed. Please try again.');
    }
  }

  // ── OPTION C: Standalone registration (Path B) ────────────────────────────
  async registerStandalone(
    companyName: string, email: string,
    password: string, plan: string = 'trial'
  ): Promise<any> {
    try {
      const response = await axios.post(`${OPTION_C_BASE}/register.php`, {
        company_name: companyName, email, password, plan
      });
      if (response.data.success) {
        // Store Option C session alongside existing tokens
        localStorage.setItem('hr_token',     response.data.token);
        localStorage.setItem('hr_tenant_id', response.data.tenant_id);
        localStorage.setItem('hr_company',   response.data.company);
        localStorage.setItem('hr_plan',      response.data.plan);
        localStorage.setItem('hr_source',    'standalone');
        return response.data;
      }
      throw new Error(response.data.error || 'Registration failed');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error)
        throw new Error(error.response.data.error);
      throw new Error('Registration failed. Please try again.');
    }
  }

  // ── OPTION C: Standalone login (Path B) ───────────────────────────────────
  async loginStandalone(email: string, password: string): Promise<any> {
    try {
      const response = await axios.post(`${OPTION_C_BASE}/login.php`, {
        email, password
      });
      if (response.data.success) {
        localStorage.setItem('hr_token',     response.data.token);
        localStorage.setItem('hr_tenant_id', response.data.tenant_id);
        localStorage.setItem('hr_company',   response.data.company);
        localStorage.setItem('hr_plan',      response.data.plan);
        localStorage.setItem('hr_source',    response.data.source);
        return response.data;
      }
      throw new Error(response.data.error || 'Login failed');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error)
        throw new Error(error.response.data.error);
      throw new Error('Login failed. Please check your credentials.');
    }
  }

  // ── OPTION C: Nexus BMP handoff verification (Path A) ─────────────────────
  async verifyNexusLink(token: string): Promise<any> {
    try {
      const response = await axios.post(`${OPTION_C_BASE}/nexus-link.php`, { token });
      if (response.data.success) {
        localStorage.setItem('hr_token',     response.data.token);
        localStorage.setItem('hr_tenant_id', response.data.tenant_id);
        localStorage.setItem('hr_company',   response.data.company);
        localStorage.setItem('hr_plan',      response.data.plan);
        localStorage.setItem('hr_source',    'nexus_linked');
        return response.data;
      }
      throw new Error(response.data.error || 'Link verification failed');
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error)
        throw new Error(error.response.data.error);
      throw new Error('Link failed. Token may have expired.');
    }
  }

  // ── Token helpers (unchanged) ─────────────────────────────────────────────
  private setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    localStorage.setItem('hr_access_token', accessToken);
    localStorage.setItem('hr_refresh_token', refreshToken);
  }

  private loadTokensFromStorage(): void {
    this.accessToken = localStorage.getItem('hr_access_token');
    this.refreshToken = localStorage.getItem('hr_refresh_token');
  }

  isAuthenticated(): boolean {
    if (!this.accessToken) return false;
    try {
      const userData = decodeToken(this.accessToken);
      return !!userData && (!userData.exp || userData.exp * 1000 > Date.now());
    } catch { return false; }
  }

  getCurrentUser(): User | null {
    if (!this.accessToken) return null;
    try {
      return decodeToken(this.accessToken);
    } catch { return null; }
  }

  logout(): void {
    this.accessToken = null;
    this.refreshToken = null;
    localStorage.removeItem('hr_access_token');
    localStorage.removeItem('hr_refresh_token');
    localStorage.removeItem('hr_token');
    localStorage.removeItem('hr_tenant_id');
    localStorage.removeItem('hr_company');
    localStorage.removeItem('hr_plan');
    localStorage.removeItem('hr_source');
  }

  getAccessToken(): string | null { return this.accessToken; }
}

export const authService = new AuthService();
